import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Grid,
  Typography,
  Link,
  IconButton,
  useTheme,
  alpha,
  Divider,
  Button,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import FacebookIcon from '@mui/icons-material/Facebook';
import XIcon from '@mui/icons-material/X';
import InstagramIcon from '@mui/icons-material/Instagram';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import GoogleIcon from '@mui/icons-material/Google';
import AppleIcon from '@mui/icons-material/Apple';
import { useTranslation } from 'react-i18next';

const StyledFooter = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' 
    ? alpha(theme.palette.background.paper, 0.9)
    : theme.palette.background.paper,
  paddingTop: theme.spacing(6),
  paddingBottom: theme.spacing(4),
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '1px',
    background: `linear-gradient(90deg, 
      transparent 0%, 
      ${alpha(theme.palette.divider, 0.2)} 25%, 
      ${alpha(theme.palette.divider, 0.2)} 75%, 
      transparent 100%)`
  }
}));

const SocialButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.text.secondary,
  '&:hover': {
    color: theme.palette.primary.main,
    transform: 'translateY(-3px)',
  },
  transition: 'all 0.2s'
}));

const FooterLink = styled(Link)(({ theme }) => ({
  color: theme.palette.text.secondary,
  textDecoration: 'none',
  '&:hover': {
    color: theme.palette.primary.main,
  },
  transition: 'color 0.2s'
}));

const StyledPlayStoreButton = styled(Button)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  backgroundColor: theme.palette.mode === 'dark' ? alpha(theme.palette.background.paper, 0.2) : alpha(theme.palette.background.default, 0.2),
  color: theme.palette.text.primary,
  padding: '8px 16px',
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
  textDecoration: 'none',
  transition: 'all 0.2s',
  marginTop: theme.spacing(2),
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
    transform: 'translateY(-2px)',
  },
}));

const Footer = () => {
  const theme = useTheme();
  const { t } = useTranslation();

  const footerSections = [
    {
      title: t('footer.company.title'),
      items: [
        { name: t('footer.company.about'), path: '/about' },
        { name: t('footer.company.odazenIntro'), path: '/tanitim' },
        { name: t('footer.company.crm'), path: '/emlak-crm' },
        { name: t('portal.footer.propertyTracking'), path: '/mulk-takibi' },
        { name: t('portal.footer.tools'), path: '/araclar' },
        { name: t('footer.company.contact'), path: '/contact' },
        { name: t('footer.company.blog'), path: '/blog' },
      ]
    },
    {
      title: t('footer.legal.title'),
      items: [
        { name: t('footer.legal.kvkk'), path: '/kvkk' },
        { name: t('footer.legal.privacy'), path: '/privacy' },
        { name: t('footer.legal.cookies'), path: '/cookies' },
        { name: t('footer.legal.terms'), path: '/terms' },
        { name: t('footer.legal.commercialConsent'), path: '/ticari-ileti-onayi' },
        { name: t('footer.legal.explicitConsent'), path: '/acik-riza' },
        { name: t('footer.legal.crmAgreement'), path: '/crm-sozlesmesi' }
      ]
    },
    {
      title: t('footer.support.title'),
      items: [
        { name: t('footer.support.help'), path: '/help' },
        { name: t('footer.support.faq'), path: '/faq' },
        { name: t('footer.support.safety'), path: '/safety' }
      ]
    }
  ];

  const socialLinks = [
    { icon: <FacebookIcon />, url: 'https://www.facebook.com/people/Odazen/61575716013184/' },
    { icon: <XIcon />, url: 'https://x.com/odazenn' },
    { icon: <InstagramIcon />, url: 'https://www.instagram.com/odazentr/' },
    { icon: <LinkedInIcon />, url: 'https://www.linkedin.com/company/odazen/' }
  ];

  return (
    <StyledFooter component="footer">
      <Container maxWidth="lg">
        <Grid container spacing={4}>
          {footerSections.map((section, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  mb: 2,
                  color: theme.palette.text.primary
                }}
              >
                {section.title}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {section.items.map((item, idx) => (
                  <FooterLink
                    key={idx}
                    component={RouterLink}
                    to={item.path}
                  >
                    {item.name}
                  </FooterLink>
                ))}
              </Box>
            </Grid>
          ))}

          <Grid item xs={12} sm={6} md={3}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                mb: 2,
                color: theme.palette.text.primary
              }}
            >
              {t('footer.social.title')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {socialLinks.map((social, index) => (
                <SocialButton
                  key={index}
                  component="a"
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {social.icon}
                </SocialButton>
              ))}
            </Box>
            
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                mt: 3,
                mb: 1,
                color: theme.palette.text.primary
              }}
            >
              {t('footer.download.title', 'Uygulamamızı İndirin')}
            </Typography>
            <StyledPlayStoreButton
              component="a"
              href="https://play.google.com/store/apps/details?id=com.craftopya.odazen"
              target="_blank"
              rel="noopener noreferrer"
              startIcon={<GoogleIcon />}
              fullWidth
            >
              {t('footer.download.playStore', 'Google Play')}
            </StyledPlayStoreButton>
            <StyledPlayStoreButton
              component="a"
              href="https://apps.apple.com/tr/app/odazen-mobil/id6753760605?l=tr"
              target="_blank"
              rel="noopener noreferrer"
              startIcon={<AppleIcon />}
              fullWidth
            >
              {t('footer.download.appStore', 'App Store')}
            </StyledPlayStoreButton>
          </Grid>
        </Grid>

        <Divider sx={{ my: 4 }} />

        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2
        }}>
          <Typography variant="body2" color="text.secondary">
            © 2024-{new Date().getFullYear()} OdaZen. {t('footer.rights')}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 3, rowGap: 1 }}>
            <FooterLink
              component={RouterLink}
              to="/terms"
              variant="body2"
            >
              {t('footer.legal.terms')}
            </FooterLink>
            <FooterLink
              component={RouterLink}
              to="/privacy"
              variant="body2"
            >
              {t('footer.legal.privacy')}
            </FooterLink>
            <FooterLink
              component={RouterLink}
              to="/kvkk"
              variant="body2"
            >
              {t('footer.legal.kvkk')}
            </FooterLink>
            <FooterLink
              component={RouterLink}
              to="/cookies"
              variant="body2"
            >
              {t('footer.legal.cookies')}
            </FooterLink>
          </Box>
        </Box>
      </Container>
    </StyledFooter>
  );
};

export default Footer; 